import qs from 'qs';
import { axiosClient } from '@/libs/axios-client';

const API_BASE = '/group-reporting';

// ============================================
// Group Reporting API
// ============================================

/**
 * Build the multipart body a run needs: the AB Soft journal exports, the period,
 * and — when a file is to be produced — the group's own template.
 *
 * @param {Object} payload - packageId, year, month, fxRate, journals, template
 * @returns {FormData} Body for preview or generate
 */
const buildRunFormData = ({
  packageId,
  year,
  month,
  fxRate,
  fxRateNote,
  journals,
  template,
  fromLedger,
}) => {
  const formData = new FormData();
  // The nightly AB Soft ledger instead of uploaded journal exports.
  if (fromLedger) formData.append('fromLedger', 'true');
  formData.append('packageId', packageId);
  formData.append('year', year);
  formData.append('month', month);
  if (fxRate !== undefined && fxRate !== null && fxRate !== '') {
    formData.append('fxRate', fxRate);
  }
  if (fxRateNote) formData.append('fxRateNote', fxRateNote);
  (journals || []).forEach((file) => formData.append('journals', file));
  if (template) formData.append('template', template);
  return formData;
};

/**
 * Get reporting packages.
 *
 * @param {Object} params - Optional companyId / isActive filters
 * @returns {Promise<Object>} Packages
 */
export const getPackages = async (params = {}) => {
  try {
    const response = await axiosClient.get(
      `${API_BASE}/packages?${qs.stringify(params)}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get reporting packages:', error);
    throw error;
  }
};

/**
 * @param {string} packageId - Package id
 * @returns {Promise<Object>} One package
 */
export const getPackageById = async (packageId) => {
  try {
    const response = await axiosClient.get(`${API_BASE}/packages/${packageId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get reporting package:', error);
    throw error;
  }
};

/**
 * When the nightly AB ledger for the package's company last arrived.
 *
 * @param {string} packageId - Package id
 * @param {number} year - Reporting year
 * @returns {Promise<Object>} receivedAt and lastPostingDate
 */
export const getPackageLedgerStatus = async (packageId, year) => {
  try {
    const response = await axiosClient.get(
      `${API_BASE}/packages/${packageId}/ledger-status?${qs.stringify({ year })}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get ledger status:', error);
    throw error;
  }
};

/**
 * @param {string} packageId - Package id
 * @param {Object} payload - Fields to change
 * @returns {Promise<Object>} Updated package
 */
export const updatePackage = async (packageId, payload) => {
  try {
    const response = await axiosClient.patch(
      `${API_BASE}/packages/${packageId}`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error('Failed to update reporting package:', error);
    throw error;
  }
};

/**
 * Get the dimension map — the translation table the accountant maintains.
 *
 * @param {Object} params - packageId, kind
 * @returns {Promise<Object>} Mappings
 */
export const getDimensionMaps = async (params = {}) => {
  try {
    const response = await axiosClient.get(
      `${API_BASE}/mappings?${qs.stringify(params)}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get dimension mappings:', error);
    throw error;
  }
};

/**
 * @param {Object} payload - Mapping fields
 * @returns {Promise<Object>} Created mapping
 */
export const createDimensionMap = async (payload) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/mappings`, payload);
    return response.data;
  } catch (error) {
    console.error('Failed to create dimension mapping:', error);
    throw error;
  }
};

/**
 * @param {string} mapId - Mapping id
 * @param {Object} payload - Fields to change
 * @returns {Promise<Object>} Updated mapping
 */
export const updateDimensionMap = async (mapId, payload) => {
  try {
    const response = await axiosClient.patch(
      `${API_BASE}/mappings/${mapId}`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error('Failed to update dimension mapping:', error);
    throw error;
  }
};

/**
 * @param {string} mapId - Mapping id
 * @returns {Promise<Object>} Retired mapping
 */
export const deleteDimensionMap = async (mapId) => {
  try {
    const response = await axiosClient.delete(`${API_BASE}/mappings/${mapId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete dimension mapping:', error);
    throw error;
  }
};

/**
 * Translate the uploaded exports and report what would happen, without saving
 * and without producing a file.
 *
 * @param {Object} payload - packageId, year, month, fxRate, journals
 * @returns {Promise<Object>} Summary, findings and totals
 */
export const previewRun = async (payload) => {
  try {
    const response = await axiosClient.post(
      `${API_BASE}/runs/preview`,
      buildRunFormData(payload),
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to preview reporting run:', error);
    throw error;
  }
};

/**
 * Fill the group's template for the period and save the run.
 *
 * The response is the workbook itself, so the run id and the number of findings
 * come back in headers rather than in a body.
 *
 * @param {Object} payload - packageId, year, month, fxRate, journals, template
 * @returns {Promise<Object>} Blob, suggested file name, run id and finding count
 */
export const generateRun = async (payload) => {
  try {
    const response = await axiosClient.post(
      `${API_BASE}/runs/generate`,
      buildRunFormData(payload),
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob',
      }
    );

    const disposition = response.headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^"]+)"?/);

    return {
      blob: response.data,
      fileName: match ? match[1] : 'group-report.xlsx',
      runId: response.headers['x-reporting-run-id'] || null,
      findingCount: Number(response.headers['x-reporting-findings'] || 0),
    };
  } catch (error) {
    console.error('Failed to generate reporting run:', error);
    throw error;
  }
};

/**
 * Fetch a workbook produced by an earlier run back out of the document store.
 *
 * @param {string} runId - Run id
 * @returns {Promise<Object>} Blob and suggested file name
 */
export const downloadRun = async (runId) => {
  try {
    const response = await axiosClient.get(
      `${API_BASE}/runs/${runId}/download`,
      {
        responseType: 'blob',
      }
    );

    const disposition = response.headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^"]+)"?/);

    return {
      blob: response.data,
      fileName: match ? match[1] : 'group-report.xlsx',
    };
  } catch (error) {
    console.error('Failed to download reporting run:', error);
    throw error;
  }
};

/**
 * @param {Object} params - companyId, packageId, year
 * @returns {Promise<Object>} Runs, newest first
 */
export const getRuns = async (params = {}) => {
  try {
    const response = await axiosClient.get(
      `${API_BASE}/runs?${qs.stringify(params)}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get reporting runs:', error);
    throw error;
  }
};

/**
 * @param {string} runId - Run id
 * @returns {Promise<Object>} Run with its lines
 */
export const getRunById = async (runId) => {
  try {
    const response = await axiosClient.get(`${API_BASE}/runs/${runId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get reporting run:', error);
    throw error;
  }
};

/**
 * Take a period back out of the frozen state, so a wrong click on "submitted"
 * does not lock the month for good.
 *
 * @param {string} runId - Run id
 * @returns {Promise<Object>} Updated run
 */
export const reopenRun = async (runId) => {
  try {
    const response = await axiosClient.patch(
      `${API_BASE}/runs/${runId}/reopen`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to reopen reporting run:', error);
    throw error;
  }
};

/**
 * Delete a period that was never sent to the group, along with its stored file.
 *
 * @param {string} runId - Run id
 * @returns {Promise<Object>} What was removed
 */
export const deleteRun = async (runId) => {
  try {
    const response = await axiosClient.delete(`${API_BASE}/runs/${runId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete reporting run:', error);
    throw error;
  }
};

/**
 * Mark a period as sent to the group, which closes it to further writes.
 *
 * @param {string} runId - Run id
 * @returns {Promise<Object>} Updated run
 */
export const submitRun = async (runId) => {
  try {
    const response = await axiosClient.patch(
      `${API_BASE}/runs/${runId}/submit`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to submit reporting run:', error);
    throw error;
  }
};

/**
 * @returns {Promise<Object>} Dimension kinds (BLOCK / UNIT / CODE)
 */
export const getDimensionKindsEnum = async () => {
  try {
    const response = await axiosClient.get(`${API_BASE}/enums/dimension-kinds`);
    return response.data;
  } catch (error) {
    console.error('Failed to get dimension kinds:', error);
    throw error;
  }
};

/**
 * @returns {Promise<Object>} Run statuses
 */
export const getStatusesEnum = async () => {
  try {
    const response = await axiosClient.get(`${API_BASE}/enums/statuses`);
    return response.data;
  } catch (error) {
    console.error('Failed to get reporting statuses:', error);
    throw error;
  }
};
